import { Component, computed, signal, ViewChild, ElementRef, inject, DestroyRef, effect, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AuthService, RegistrationErrorResponse, RegistrationRequest } from '@core/services/auth';
import { UserContactDetailsRequest, UserService } from '@core/services/user';
import { Gradientsegmentbar } from '@shared/components/gradientsegmentbar/gradientsegmentbar';
import { PhoneValidators } from '@shared/validators/phone.validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
/* MDBootstrap modules */
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbLoadingModule } from 'mdb-angular-ui-kit/loading';
import { MdbRippleModule } from 'mdb-angular-ui-kit/ripple';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { finalize, switchMap } from 'rxjs/operators';
import { MdbCheckboxModule } from "mdb-angular-ui-kit/checkbox";
import { Announcement } from "@shared/components/announcement/announcement";
import { AnnouncementResponse, AnnouncementService } from '@core/services/announcement';
import { clearOtpAttempts, isOtpLocked, recordOtpFailure } from '@shared/utilities/otp-attempt.util';
import { environment } from 'src/environments/environment';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';

//#region Enums 
export type AuthForm = 'login' | 'register' | 'verification';
export type VerificationSource = 'login' | 'register';
//#endregion

//#region Constants
const LOADING_TRANSITION_DELAY = 300;
const ALERT_DISPLAY_DURATION = 30000;
const REDIRECT_ALERT_DISPLAY_DURATION = 10000;
const VERIFICATION_CODE_LENGTH = 6;
const RESEND_CODE_COUNTDOWN_SECONDS = 30;
//#endregion

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    MdbFormsModule,
    ReactiveFormsModule,
    MdbRippleModule,
    MdbValidationModule,
    Gradientsegmentbar,
    MdbLoadingModule,
    MdbCheckboxModule,
    Announcement,
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})

export class Login {
  loadingContainer = viewChild<ElementRef>('loadingContainer');
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);
  private announcementService = inject(AnnouncementService);
  private route = inject(ActivatedRoute);

  announcementData = signal<AnnouncementResponse | null>(null);
  // Use with takeUntilDestroyed for unsubscribe
  private destroyRef = inject(DestroyRef);
  //#region UI State
  currentForm = signal<AuthForm>('login');
  verificationSource = signal<VerificationSource>('login');
  verificationContact = signal<string>('');
  isLoading = signal<boolean>(false);
  cardClass = computed(() => {
    const form = this.currentForm();
    if (form === 'verification') return 'verification';
    if (form === 'register') return 'expanded';
    return '';
  });
  //display error message on the login form - originally used for the redirect after a session has expired
  //to handle 401 status codes
  errorState = signal<{ type: 'error' | 'success' | 'info' | 'warning'; message: string; } | null>(null);
  //set styling for the alert message on the form
  //used to handle api error messages - not meant to be used with form validation. 
  alertState = signal<{ type: 'error' | 'success' | 'info' | 'warning'; message: string; } | null>(null);

  /* Constructor */
  // Auto-dismiss effect for alert
  constructor() {
    const errorMessage = this.errorState();
    //get query param
    this.route.queryParamMap.pipe(takeUntilDestroyed())
      .subscribe(params => {
        const errorMessage = params.get('err');
        if (errorMessage) {
          this.errorState.set({ type: 'warning', message: errorMessage });
          //when set add a timeout to hide the displayed error
          setTimeout(() => {
            this.errorState.set(null);
          }, REDIRECT_ALERT_DISPLAY_DURATION);
        }
      });

    effect(() => {
      const alert = this.alertState();
      if (alert) {
        setTimeout(() => {
          if (this.alertState() === alert) {
            this.alertState.set(null);
          }
        }, ALERT_DISPLAY_DURATION);
      }
    });

    // Register cleanup for countdown interval
    this.destroyRef.onDestroy(() => {
      if (this.resendInterval) {
        clearInterval(this.resendInterval);
      }
    });

    this.announcementService.getAllAnnouncements().subscribe({
      next: (data) => {
        this.announcementData.set(data ?? null);
      },
      error: (err) => {
        console.error('Dashboard - Announcements error:', err);
      }
    });
    console.log(environment.recaptchaSiteKey);

  }

  // Resend code countdown timer
  resendCountdown = signal<number>(0);
  resendInFlight = signal<boolean>(false);
  private resendInterval: any = null;
  // Computed for button text
  resendButtonText = computed(() => {
    const countdown = this.resendCountdown();
    return countdown > 0 ? `Send New Code (${countdown})` : 'Send New Code';
  });


  //#region RECAPTCHA
  private recaptchaV3Service = inject(ReCaptchaV3Service);
  siteKey = environment.recaptchaSiteKey;
  //#endregion

  //#endregion

  //#region Form Navigation
  //set displayed form
  showRegistrationForm() {
    //clear the alert when navigating to another form
    this.alertState.set(null);
    this.currentForm.set('register');
    // Move focus to the new screen's heading
    setTimeout(() => {
      const heading = document.querySelector('#registrationScreen h2') as HTMLElement;
      heading?.focus();
    }, 50);
  }
  showLoginForm() {
    //clear the alert when navigating to another form
    this.alertState.set(null);
    this.currentForm.set('login');
    this.clearRegistrationForm();
    setTimeout(() => {
      const heading = document.querySelector('#loginScreen h2') as HTMLElement;
      heading?.focus();
    }, 50);
  }
  backFromVerification() {
    //clear the alert when navigating to another form
    this.alertState.set(null);

    // Clear countdown when leaving verification
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
    this.resendCountdown.set(0);

    // Don't clear an active lockout on back navigation
    if (!isOtpLocked()) {
      clearOtpAttempts();
      this.otpLocked.set(false);
    }

    const source = this.verificationSource();
    this.clearVerificationCode();
    if (source === 'login') {
      this.currentForm.set('login');
      setTimeout(() => {
        const heading = document.querySelector('#loginScreen h2') as HTMLElement;
        heading?.focus();
      }, 50);
    } else {
      this.currentForm.set('register');
      setTimeout(() => {
        const heading = document.querySelector('#registrationScreen h2') as HTMLElement;
        heading?.focus();
      }, 50);
    }
  }
  //#endregion

  //#region  Login Form
  loginForm = new FormGroup({
    email: new FormControl('', this.emailOrPhoneValidator.bind(this))
  });
  get email(): AbstractControl {
    return this.loginForm.get('email')!;
  }
  // Login
  async handleLogin(event: Event) {
    event.preventDefault();
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.alertState.set(null);

    const emailOrPhone = this.loginForm.value.email!;

    this.recaptchaV3Service.execute('login').subscribe({
      next: (captchaToken: string) => {
        // Navigate immediately — don't wait for the response
        this.isLoading.set(false);
        this.verificationContact.set(emailOrPhone);
        this.verificationSource.set('login');
        this.currentForm.set('verification');
        setTimeout(() => {
          const heading = document.querySelector('#verificationScreen h2') as HTMLElement;
          heading?.focus();
        }, 50);
        this.startResendCountdown(RESEND_CODE_COUNTDOWN_SECONDS);

        if (isOtpLocked()) {
          return;
        }

        this.authService.sendVerificationCode(emailOrPhone, captchaToken)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            error: (httpErrorResponse: HttpErrorResponse) => {
              this.currentForm.set('login');
              this.verificationSource.set('login');
              this.verificationContact.set(emailOrPhone);

              let errorMessage = 'There was an issue sending the verification code. Please try again.';
              if (httpErrorResponse.status === 404) {
                errorMessage = 'Error looking up account. Please verify the email or phone number entered and try again.';
              }
              this.alertState.set({ type: 'error', message: errorMessage });
            }
          });
      },
      error: () => {
        this.isLoading.set(false);
        this.alertState.set({ type: 'error', message: 'Verification failed. Please try again.' });
      }
    });
  }

  //Displayed error dependent on validation error - invalid email or failed phone validation
  private emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const controlVal = control.value;
    //null check
    if (!controlVal) {
      return { required: true };
    }
    //email validation
    if (controlVal.includes('@')) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(controlVal) ? null : { invalidEmail: true };
    }
    else {
      return PhoneValidators.usaPhone(control);
    }
  }
  //#endregion

  //#region Registration Form
  registrationForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.email, this.contactPairValidator('email')]),
    phone: new FormControl('', [PhoneValidators.usaPhone, this.contactPairValidator('phone')]),
    attorneyCheck: new FormControl(false, Validators.requiredTrue),
    termsCheck: new FormControl(false, Validators.requiredTrue),
    agreeSMSCheck: new FormControl(false)
  }, { validators: this.atLeastOneContactValidator.bind(this) });
  // Registration form getters
  get firstName(): AbstractControl {
    return this.registrationForm.get('firstName')!;
  }
  get lastName(): AbstractControl {
    return this.registrationForm.get('lastName')!;
  }
  get regEmail(): AbstractControl {
    return this.registrationForm.get('email')!;
  }
  get phone(): AbstractControl {
    return this.registrationForm.get('phone')!;
  }
  get attorneyCheck(): AbstractControl {
    return this.registrationForm.get('attorneyCheck')!;
  }
  get termsCheck(): AbstractControl {
    return this.registrationForm.get('termsCheck')!;
  }
  get agreeSMSCheck(): AbstractControl {
    return this.registrationForm.get('agreeSMSCheck')!;
  }

  // Custom validator: at least one contact method required
  private atLeastOneContactValidator(group: AbstractControl): ValidationErrors | null {
    const email = group.get('email')?.value;
    const phone = group.get('phone')?.value;
    return email || phone ? null : { atLeastOneContact: true };
  }

  //Phone & Email validator
  // Single validator for both phone email non-empty
  private contactPairValidator(currentField: 'email' | 'phone'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // If current field has a value, it's valid
      if (control.value?.trim()) {
        return null;
      }

      // check the email/phone field for content
      const pairedFieldName = currentField === 'email' ? 'phone' : 'email';
      const otherControl = control.parent?.get(pairedFieldName);

      // If other field has a value, current field can be empty
      if (otherControl?.value?.trim()) {
        return null;
      }

      // If both are touched/dirty and both are empty, show error
      if ((control.dirty || control.touched) && (otherControl?.dirty || otherControl?.touched)) {
        return { contactRequired: true };
      }

      return null;
    };
  }

  // Registration
  async createAccount(event: Event) {
    event.preventDefault();
    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();
      this.regEmail.updateValueAndValidity();
      this.phone.updateValueAndValidity();
      return;
    }

    this.isLoading.set(true);
    this.alertState.set(null);

    const formValue = this.registrationForm.value;

    this.recaptchaV3Service.execute('register').subscribe({
      next: (captchaToken: string) => {
        const userData: RegistrationRequest = {
          firstName: formValue.firstName!,
          lastName: formValue.lastName!,
          email: formValue.email!,
          phoneNumber: formValue.phone!,
          attorneyConfirmed: formValue.attorneyCheck!,
          termsAccepted: formValue.termsCheck!,
          recaptchaToken: captchaToken,
        };

        this.authService.register(userData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response) => {
            if (!response.success) {
              this.isLoading.set(false);
              this.alertState.set({
                type: 'error',
                message: response.message || 'Registration failed. Please try again.'
              });
              return;
            }
            const contact = formValue.email || formValue.phone;
            this.verificationContact.set(contact!);
            this.verificationSource.set('register');
            this.currentForm.set('verification');
            setTimeout(() => {
              const heading = document.querySelector('#verificationScreen h2') as HTMLElement;
              heading?.focus();
            }, 50);
            this.startResendCountdown(RESEND_CODE_COUNTDOWN_SECONDS);
            setTimeout(() => this.isLoading.set(false), LOADING_TRANSITION_DELAY);
          },
          error: (httpErrorResponse: HttpErrorResponse) => {
            this.isLoading.set(false);
            const registrationError = httpErrorResponse.error as RegistrationErrorResponse;
            let errorMessage = 'Registration failed. Please try again.';
            if (httpErrorResponse.status === 400) {
              errorMessage = registrationError.detail || 'Please check your information and try again.';
            } else if (httpErrorResponse.status === 500) {
              errorMessage = 'Server error. Please try again later.';
            }
            this.alertState.set({ type: 'error', message: errorMessage });
          }
        });
      },
      error: () => {
        this.isLoading.set(false);
        this.alertState.set({ type: 'error', message: 'Verification failed. Please try again.' });
      }
    });
  }
  //#endregion

  //#region Verification Code Form
  //for use by template expose isOtpLocked from the util interface
  protected readonly isOtpLocked = isOtpLocked;
  otpLocked = signal<boolean>(false);
  verificationCode = signal(['', '', '', '', '', '']);

  //check all fields set
  isVerificationCodeComplete = computed(() => {
    return this.verificationCode().every(digit => digit !== '');
  });

  //displayed message on verification code screen
  verificationMessage = computed(() => {
    const contact = this.verificationContact();
    const isEmail = contact.includes('@');
    if (isEmail) {
      return `Please enter the code we sent to your email <br/> <strong>${contact}</strong>`;
    } else {
      const formattedPhoneNumber = this.formatPhoneNumber(contact);
      return `Please enter the code we sent to your phone number <br/> <strong>${formattedPhoneNumber}</strong>`;
    }
  });

  async submitVerificationCode(event: Event) {
    event.preventDefault();
    //prevent submission if locked
    if (isOtpLocked()) {
      return;
    }

    const code = this.verificationCode().join('');
    if (code.length !== VERIFICATION_CODE_LENGTH) {
      return;
    }
    this.isLoading.set(true);

    this.authService.verifyCode(code, this.verificationContact())
      .pipe(
        switchMap((response) => {
          this.authService.markAuthenticated();

          // Determine if it's email or phone, same logic as verifyCode
          const emailOrPhone = this.verificationContact();
          const getUserRequest: UserContactDetailsRequest = {
            email: emailOrPhone.includes('@') ? emailOrPhone : '',
            phoneNumber: emailOrPhone.includes('@') ? '' : emailOrPhone
          };
          return this.userService.getUserInfo(getUserRequest);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (userInfo) => {
          // Store basic user info for demo
          this.userService.saveUserInfo({
            userId: userInfo.userId,
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            email: userInfo.email,
            phoneNumber: userInfo.phoneNumber
          });
          //clear bad attempts on success
          clearOtpAttempts();
          this.router.navigate(['/dashboard']);
        },
        error: (httpErrorResponse: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.clearVerificationCode();
          const { locked, remaining } = recordOtpFailure();
          if (locked) {
            this.otpLocked.set(true);
          }
          else {
            let errorMessage = 'The code entered was either invalid or expired. Please enter the correct code or request another.';
            if (httpErrorResponse.status === 404) {
              errorMessage = 'Error looking up account. Please verify the email or phone number entered and try again.';
            }

            this.alertState.set({
              type: 'error',
              message: errorMessage
            });
          }

        }
      });
  }

  async resendCode() {
    // Enforce the resend lock in code as well as in the template so rapid,
    // repeated, or programmatic clicks cannot create concurrent OTP requests.
    if (this.resendCountdown() > 0 || isOtpLocked() || this.resendInFlight()) {
      return;
    }

    this.resendInFlight.set(true);
    // Start the cooldown before CAPTCHA/API work begins, closing the window in
    // which a second click could launch another request.
    this.startResendCountdown(RESEND_CODE_COUNTDOWN_SECONDS);
    this.isLoading.set(true);
    this.clearVerificationCode();
    // Only reset attempts if the lockout has already expired naturally
    if (!isOtpLocked()) {
      clearOtpAttempts();
      this.otpLocked.set(false);
    }

    this.recaptchaV3Service.execute('login').pipe(
      switchMap((captchaToken: string) =>
        this.authService.resendVerificationCode(this.verificationContact(), captchaToken)
      ),
      finalize(() => {
        this.isLoading.set(false);
        this.resendInFlight.set(false);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.alertState.set({
          type: 'success',
          message: 'New verification code sent successfully!'
        });
      },
      error: (error: unknown) => {
        let errorMessage = 'There was an issue when making your request. Please try again.';

        if (error instanceof HttpErrorResponse && error.status === 404) {
          errorMessage = 'Error looking up account. Please verify the email or phone number entered and try again.';
        }

        this.alertState.set({
          type: 'error',
          message: errorMessage
        });
      }
    });
  }

  clearRegistrationForm() {
    this.registrationForm.reset();

  }

  clearVerificationCode() {
    this.verificationCode.set(['', '', '', '', '', '']);
  }

  //#region Verification Code Events
  onCodeInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Take only the last character entered
    const lastChar = value.slice(-1);
    // Clear the input and set it to just the last digit
    if (!/^\d$/.test(lastChar)) {
      input.value = '';
      const current = this.verificationCode();
      current[index] = '';
      this.verificationCode.set([...current]);
      return;
    }
    input.value = lastChar;
    const current = this.verificationCode();
    current[index] = lastChar;
    this.verificationCode.set([...current]);
    if (lastChar && index < 5) {
      const nextInput = document.querySelectorAll('.code-input')[index + 1] as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  }

  onCodeKeydown(index: number, event: KeyboardEvent) {
    // Allow paste shortcuts (Ctrl+V or Cmd+V)
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      return;
    }
    // Allow: backspace, delete, tab, escape, enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      if (event.key === 'Backspace' && !this.verificationCode()[index] && index > 0) {
        const prevInput = document.querySelectorAll('.code-input')[index - 1] as HTMLInputElement;
        if (prevInput) prevInput.focus();
      }
      return;
    }
    // Prevent if not a number 0-9
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
    //continue code entry otherwise
  }

  onCodePaste(index: number, event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text').replace(/\D/g, '') || '';
    // Create a copy, then modify the copy
    // causes the computed value to re evaluate
    const current = [...this.verificationCode()];
    Array.from(pastedData).forEach((char, i) => {
      if (i < 6) {
        current[i] = char;
      }
    });

    // Set the modified copy
    this.verificationCode.set(current);
    // Update the actual input elements
    document.querySelectorAll('.code-input').forEach((input, i) => {
      (input as HTMLInputElement).value = current[i] || '';
    });
    const nextEmpty = current.findIndex(v => !v);
    const targetIndex = nextEmpty !== -1 ? nextEmpty : 5;
    const targetInput = document.querySelectorAll('.code-input')[targetIndex] as HTMLInputElement;
    if (targetInput) {
      targetInput.focus();
    }
  }
  //Verification Code Resend Code countdown timer
  private startResendCountdown(seconds: number) {
    // Clear any existing interval
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }

    this.resendCountdown.set(seconds);

    this.resendInterval = setInterval(() => {
      const current = this.resendCountdown();
      if (current > 0) {
        this.resendCountdown.set(current - 1);
      } else {
        clearInterval(this.resendInterval);
        this.resendInterval = null;
      }
    }, 1000);
  }
  //#endregion

  //Utility
  private formatPhoneNumber(phone: string): string {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // Format as (555) 555-5555
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // Return original if not 10 digits
    return phone;
  }


}
